import { SSE } from 'sse.js';
import globals from '../../common/globals';
import { safeJsonParse } from '@common/utils';
import { getCustomHeader } from '@common/clerkAuth';

export type FileContext = {
  name: string;
  content: string;
  type: 'json' | 'text' | 'selection';
}

export type SelectedCode = {
  /** Base 1 line number of the selected code. */
  startLine: number;
  /** Base 1 line number of the end of the selected code. */
  endLine: number;
  /** Base 1 column number of the selected code. */
  startColumn: number;
  /** Base 1 column number of the end of the selected code. */
  endColumn: number;
}

export type CodeGenRequest = {
  sessionId?: string;
  /** The main prompt for code generation. */
  prompt: string;
  /** Optional existing user code to provide context. */
  userCode?: string;
  // selectedCode?: SelectedCode;
  filesContext?: FileContext[];
};

/**
 * @description Generic function to call an SSE endpoint and handle its lifecycle.
 * @template TRequestPayload The type of the request payload.
 * @param {object} config - Configuration for the SSE call.
 * @param {string} config.url - The endpoint URL.
 * @param {TRequestPayload} config.payload - The request payload.
 * @param {string} config.userId - The user ID (for reference, not used in headers).
 * @param {(sessionId: string) => void} config.onSession - Callback for when a session ID is received.
 * @param {(chunk: string) => void} config.onChunk - Callback for when a code chunk is received.
 * @returns {Promise<any>} A promise that resolves with the final response event or rejects on error/abort.
 */
const callSseEndpoint = async <TRequestPayload>(config: {
  url: string;
  payload: TRequestPayload;
  userId: string;
  onSession?: (sessionId: string) => void;
  onChunk: (chunk: string) => void;
}): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    // Get Clerk authentication headers
    const authHeaders = await getCustomHeader();
    
    const sse = new SSE(config.url, {
      method: 'POST',
      payload: JSON.stringify(config.payload),
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });

    sse.addEventListener('session', (event: any) => {
      console.log('SSE session: ', event);
      const data = JSON.parse(event.data);
      config.onSession?.(data.sessionId);
    });

    sse.addEventListener('chunk', (event: any) => {
      config.onChunk(event.data);
    });

    sse.addEventListener('finalResponse', (event: any) => {
      resolve(event);
    });

    sse.onerror = (error: any) => {
      console.error('SSE error:', error);
      const parsedError = safeJsonParse(error.data);
      reject(parsedError || error);
    };

    sse.onabort = (event: any) => {
      console.log('SSE connection aborted: ', event);
      reject(new Error('SSE connection aborted'));
    };

    sse.stream();
  });
};

/**
 * @description Base configuration for SSE code generation functions.
 * @property {string} prompt - The main prompt for code generation.
 * @property {string} [sessionId] - Optional session ID to continue a previous generation.
 * @property {(sessionId: string) => void} onSession - Callback for when a session ID is received.
 * @property {(chunk: string) => void} onChunk - Callback for when a code chunk is received.
 */
export type BaseCodeGenConfig = {
  prompt: string;
  sessionId?: string;
  onSession?: (sessionId: string) => void;
  onChunk: (chunk: string) => void;
};

/**
 * @description Generates script code via SSE.
 * @param {object} config - Configuration for script code generation.
 * @param {string} config.currentCode - The current code in the editor.
 * @param {FileContext[]} [config.filesContext] - Optional context from other files.
 * // Inherited properties from BaseCodeGenConfig:
 * @param {string} config.prompt - The prompt for code generation.
 * @param {string} [config.sessionId] - Optional session ID.
 * @param {string} config.userId - The user ID.
 * @param {(session: string) => void} config.onSession - Callback for session ID.
 * @param {(chunk: string) => void} config.onChunk - Callback for code chunks.
 * @returns {Promise<void>} A promise that resolves when the SSE stream is finished or an error occurs.
 */
const scriptCodeGen = async (config: BaseCodeGenConfig & {
  currentCode: string;
  filesContext?: FileContext[];
  userId: string;
}): Promise<void> => {
  const request: CodeGenRequest = {
    userCode: config.currentCode,
    prompt: config.prompt,
    sessionId: config.sessionId,
    filesContext: config.filesContext,
  };

  const url = `${globals.PLATFORM_API}/v1/script/code-gen`;

  return callSseEndpoint<CodeGenRequest>({
    url,
    payload: request,
    userId: config.userId,
    onSession: config.onSession,
    onChunk: config.onChunk,
  });
};

export type ValueProcessorCodeGenRequest = {
  /** The main prompt for code generation. */
  prompt: string;
  /** The input value to be processed, as a string. */
  inputValue?: string | Record<string, unknown> | number | boolean;
  /** The type of the input value. */
  inputValueType: 'json' | 'text';
};

// Internal payload type for valueProcessorGen, includes sessionId
type ValueProcessorGenPayload = ValueProcessorCodeGenRequest & {
  sessionId?: string;
};

export type IntegrationGenRequest = {
  /** The main prompt for integration code generation. */
  prompt: string;
};

// Internal payload type for integrationGen, includes sessionId
type IntegrationGenPayload = IntegrationGenRequest & {
  sessionId?: string;
};

/**
 * @description Generates value processor code via SSE.
 * @param {object} config - Configuration for value processor code generation.
 * @param {'json' | 'text'} config.inputValueType - The type of the input value.
 * // Inherited properties from BaseCodeGenConfig:
 * @param {string} config.prompt - The prompt for code generation.
 * @param {string} [config.sessionId] - Optional session ID.
 * @param {string} config.userId - The user ID.
 * @param {(session: string) => void} config.onSession - Callback for session ID.
 * @param {(chunk: string) => void} config.onChunk - Callback for code chunks.
 * @returns {Promise<void>} A promise that resolves when the SSE stream is finished or an error occurs.
 */
const valueProcessorGen = async (config: BaseCodeGenConfig & {
  inputValue: string | number | boolean | Record<string, unknown> | undefined;
  inputValueType: 'json' | 'text';
  userId: string;
}): Promise<void> => {
  const request: ValueProcessorGenPayload = {
    prompt: config.prompt,
    inputValue: config.inputValue,
    inputValueType: config.inputValueType,
    sessionId: config.sessionId,
  };

  const url = `${globals.PLATFORM_API}/v1/script/value-processor-gen`;

  return callSseEndpoint<ValueProcessorGenPayload>({
    url,
    payload: request,
    userId: config.userId,
    onSession: config.onSession,
    onChunk: config.onChunk,
  });
};

/**
 * Type for the text generation request payload.
 */
export type TextGenRequest = {
  /**
   * The main instruction or question for the text generation task.
   */
  prompt: string;
  /**
   * Optional existing text content that the user might want the AI to modify, extend, or use as context.
   * For example, an existing email draft to be rewritten or a paragraph to be summarized.
   */
  existingText?: string;
  /**
   * Optional JSON data (as a string) that provides structured information for template generation or data-driven text.
   * For example, a JSON object with user details to populate a mustache template.
   */
  jsonData?: string;
};

/**
 * @description Generates text via SSE.
 * @param {object} config - Configuration for text generation.
 * @param {string} [config.existingText] - Optional existing text content.
 * @param {string} [config.jsonData] - Optional JSON data as a string.
 * // Inherited properties from BaseCodeGenConfig:
 * @param {string} config.prompt - The prompt for text generation.
 * @param {string} [config.sessionId] - Optional session ID.
 * @param {string} config.userId - The user ID.
 * @param {(chunk: string) => void} config.onChunk - Callback for code chunks.
 * @returns {Promise<void>} A promise that resolves when the SSE stream is finished or an error occurs.
 */
const textGen = async (config: BaseCodeGenConfig & {
  existingText?: string;
  jsonData?: string;
  userId: string;
}): Promise<void> => {
  const request: TextGenRequest = {
    prompt: config.prompt,
    existingText: config.existingText,
    jsonData: config.jsonData,
  };

  const url = `${globals.PLATFORM_API}/v1/text/gen`;

  return callSseEndpoint<TextGenRequest>({
    url,
    payload: request,
    userId: config.userId,
    onChunk: config.onChunk,
  });
};

/**
 * @description Generates integration script code via SSE.
 * @param {object} config - Configuration for integration code generation.
 * // Inherited properties from BaseCodeGenConfig:
 * @param {string} config.prompt - The prompt for integration code generation.
 * @param {string} [config.sessionId] - Optional session ID.
 * @param {string} config.userId - The user ID.
 * @param {(session: string) => void} config.onSession - Callback for session ID.
 * @param {(chunk: string) => void} config.onChunk - Callback for code chunks.
 * @returns {Promise<void>} A promise that resolves when the SSE stream is finished or an error occurs.
 */
const integrationGen = async (config: BaseCodeGenConfig & {
  userId: string;
}): Promise<void> => {
  const request: IntegrationGenPayload = {
    prompt: config.prompt,
    sessionId: config.sessionId,
  };

  const url = `${globals.PLATFORM_API}/v1/script/integration-gen`;

  return callSseEndpoint<IntegrationGenPayload>({
    url,
    payload: request,
    userId: config.userId,
    onSession: config.onSession,
    onChunk: config.onChunk,
  });
};

const platformApi = {
  scriptCodeGen,
  valueProcessorGen,
  textGen,
  integrationGen,
};

export default platformApi;

